import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

import cn from '@/utils/cn';

// ─── Types ────────────────────────────────────────────────────────────────────
type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  toasts: Toast[];
  toast: {
    success: (message: string) => void;
    error: (message: string) => void;
    warning: (message: string) => void;
    info: (message: string) => void;
  };
  dismiss: (id: string) => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────
const ToastContext = createContext<ToastContextValue | null>(null);

// ─── Icons & styles ───────────────────────────────────────────────────────────
const toastConfig: Record<
  ToastType,
  { icon: typeof CheckCircle; containerClass: string; iconClass: string }
> = {
  success: {
    icon: CheckCircle,
    containerClass: 'border-success/30 bg-success-tint',
    iconClass: 'text-success',
  },
  error: {
    icon: XCircle,
    containerClass: 'border-danger/30 bg-danger-tint',
    iconClass: 'text-danger',
  },
  warning: {
    icon: AlertTriangle,
    containerClass: 'border-warning/40 bg-warning-tint',
    iconClass: 'text-warning',
  },
  info: {
    icon: Info,
    containerClass: 'border-brand/30 bg-brand-tint',
    iconClass: 'text-brand',
  },
};

// ─── Provider ─────────────────────────────────────────────────────────────────
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const add = useCallback((type: ToastType, message: string) => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useMemo(
    () => ({
      success: (message: string) => add('success', message),
      error: (message: string) => add('error', message),
      warning: (message: string) => add('warning', message),
      info: (message: string) => add('info', message),
    }),
    [add],
  );

  const contextValue = useMemo(() => ({ toasts, toast, dismiss }), [toasts, toast, dismiss]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {/* Toast renderer */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:inset-x-auto sm:right-4 sm:items-end sm:px-0"
      >
        {toasts.map((t) => {
          const { icon: Icon, containerClass, iconClass } = toastConfig[t.type];
          return (
            <div
              key={t.id}
              className={cn(
                'pointer-events-auto flex w-full max-w-80 items-start gap-3 rounded-lg border p-4 shadow-md sm:w-80',
                'animate-in slide-in-from-right-4 fade-in duration-200',
                containerClass,
              )}
            >
              <Icon size={18} className={cn('mt-0.5 shrink-0', iconClass)} />
              <p className="flex-1 text-sm text-ink">{t.message}</p>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                className="-m-2 grid size-11 shrink-0 place-items-center rounded-md text-ink/40 hover:text-ink/70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:size-9"
                aria-label="Dismiss notification"
              >
                <X size={16} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
};
